/**
 * IntegrationSetupGuide Component
 *
 * Renders numbered setup steps for an integration,
 * following the same visual pattern as GitHubActionSetupWizard.
 */
import React from 'react';
import {
  Text,
  Link,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { IntegrationSetupData } from '../constants/setupInstructions';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
  },
  codeBlock: {
    fontFamily: 'monospace',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
  },
});

export interface IntegrationSetupGuideProps {
  data: IntegrationSetupData;
}

export const IntegrationSetupGuide: React.FC<IntegrationSetupGuideProps> = ({ data }) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <MessageBar intent="info">
        <MessageBarBody>{data.intro}</MessageBarBody>
      </MessageBar>

      {data.steps.map((step, index) => (
        <div key={index} className={styles.step}>
          <div className={styles.stepHeader}>
            <span className={styles.stepNumber}>{index + 1}</span>
            <Text weight="semibold">{step.title}</Text>
          </div>
          <Text size={200}>{step.description}</Text>
          {step.link && (
            <Link href={step.link.url} target="_blank" rel="noopener noreferrer">
              {step.link.label}
            </Link>
          )}
          {step.code && (
            <div className={styles.codeBlock}>
              <Text size={200}>{step.code}</Text>
            </div>
          )}
        </div>
      ))}

      {data.tip && (
        <MessageBar intent="warning">
          <MessageBarBody>{data.tip}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};
